import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/styles';
import { useContextSwitcher } from '@/hooks/context/useContextSwitcher';

/**
 * A single option in the dark-themed context dropdown. Built on the Radix
 * primitive (not the design-system `SelectItem`) so we can drop the left
 * checkmark and mark the active row with a background highlight instead —
 * matching the sidebar design (DE-336). The "Latest" pill flags the context
 * holding the most recent results (DE-702).
 */
const ContextSwitcherOption = ({
  value,
  label,
  showLatest,
  testid,
}: {
  value: string;
  label: string;
  showLatest: boolean;
  testid: string;
}) => (
  <SelectPrimitive.Item
    value={value}
    data-testid={testid}
    className={cn(
      'relative flex w-full cursor-pointer select-none items-center justify-between gap-3',
      'px-5 py-3 text-sm text-sidebar-primary-foreground outline-none',
      'data-[highlighted]:bg-white/[0.06] data-[state=checked]:bg-white/10',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
    )}
  >
    <SelectPrimitive.ItemText>{label}</SelectPrimitive.ItemText>
    {showLatest && (
      <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-xs text-sidebar-primary-foreground">
        Latest
      </span>
    )}
  </SelectPrimitive.Item>
);

/**
 * Live in-app switch between the Consumer experience and any partner-org
 * experiences (DE-336). Only rendered when there's actually more than one
 * experience to switch between.
 */
export const DashboardLayoutContextSwitcher = () => {
  const { canSwitch, options, currentValue, selectValue } = useContextSwitcher();

  if (!canSwitch) return null;

  return (
    <div className="flex flex-col gap-2">
      <span className="mx-1 text-xs font-medium uppercase tracking-wider text-white">
        Test Results
      </span>
      <SelectPrimitive.Root value={currentValue} onValueChange={selectValue}>
        <SelectPrimitive.Trigger
          className={cn(
            'group flex h-12 w-full items-center justify-between gap-2 rounded-xs',
            'bg-sidebar-primary px-5 text-sm text-sidebar-primary-foreground outline-none',
            'focus:ring-1 focus:ring-sidebar-border',
          )}
          aria-label="Switch experience"
          data-testid="dashboardSidebar-contextSwitcher"
        >
          <SelectPrimitive.Value />
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="h-4 w-4 opacity-70 transition-transform group-data-[state=open]:rotate-180" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            side="top"
            sideOffset={8}
            className={cn(
              'z-50 min-w-[var(--radix-select-trigger-width)] max-h-[var(--radix-select-content-available-height)] overflow-y-auto rounded-xs',
              'border border-sidebar-border bg-sidebar-primary shadow-lg',
              'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            )}
          >
            <SelectPrimitive.Viewport className="flex flex-col">
              {options.map(({ value, label, showLatest }) => (
                <ContextSwitcherOption
                  key={value}
                  value={value}
                  label={label}
                  showLatest={showLatest}
                  testid={`dashboardSidebar-contextSwitcher-option-${value}`}
                />
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </div>
  );
};
